import { useMemo, useState } from "react";
import { Icon } from "../ui/Icon";
import { STATUS_META, type TaskStatus } from "./taskMeta";
import "./Calendar.css";

/**
 * Aylık takvim — Faz 2.
 *
 * Görev, planlanan başlangıç–bitiş aralığındaki HER güne düşer (tek günlük görev
 * dahil). Tarihler YEREL gün olarak karşılaştırılır; UTC'ye çevirmek UTC+3'te
 * görevleri bir gün kaydırırdı (modülün tarih sözleşmesi yerel gece yarısı).
 */

export interface CalTask {
  id: string;
  code: string;
  title: string;
  status: string;
  plannedStart: string | Date | null;
  plannedEnd: string | Date | null;
  isMilestone: boolean;
}

interface Props {
  tasks: CalTask[];
  onOpenTask: (id: string) => void;
}

const DOW = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const MAX_PER_DAY = 3;

function ymd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function CalendarView({ tasks, onOpenTask }: Props) {
  const today = new Date();
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [dayOpen, setDayOpen] = useState<string | null>(null); // "tümünü göster" açılan gün

  /** Ay ızgarası: pazartesi başlangıçlı 6 hafta (sabit yükseklik → ay değişince zıplamaz). */
  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const shift = (first.getDay() + 6) % 7; // Pazar=0 → Pazartesi başlangıcına çevir
    const start = new Date(first.getFullYear(), first.getMonth(), 1 - shift);
    return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }, [cursor]);

  /**
   * gün → o gün AKTİF olan görevler.
   *
   * Yalnız GÖRÜNEN 42 güne yazılır. Önce görevin tüm aralığı gün gün geziliyordu
   * ve 400 iterasyonluk bir emniyet sınırı vardı; 2 yıllık bir görev sessizce
   * kesiliyor, sonraki aylarda takvimde HİÇ görünmüyordu (uyarı da yoktu).
   * Görünür pencereyle kesişim hem sınırı gereksiz kılar hem de maliyeti sabitler.
   */
  const byDay = useMemo(() => {
    const map = new Map<string, CalTask[]>();
    if (grid.length === 0) return map;
    const gStart = grid[0]!;
    const gEnd = grid[grid.length - 1]!;
    for (const t of tasks) {
      if (!t.plannedStart && !t.plannedEnd) continue;
      const sRaw = new Date(t.plannedStart ?? t.plannedEnd!);
      const eRaw = new Date(t.plannedEnd ?? t.plannedStart!);
      const s = new Date(sRaw.getFullYear(), sRaw.getMonth(), sRaw.getDate());
      // Bozuk aralık (bitiş < başlangıç): görev takvimden TAMAMEN kaybolmasın —
      // en az başlangıç gününde görünsün, yoksa kullanıcı "planlanmamış" sanıyordu.
      const eLocal = new Date(eRaw.getFullYear(), eRaw.getMonth(), eRaw.getDate());
      const e = eLocal < s ? s : eLocal;
      // Görünür pencereye kırp.
      const from = s < gStart ? gStart : s;
      const to = e > gEnd ? gEnd : e;
      if (from > to) continue; // bu ayda hiç görünmüyor
      const cur = new Date(from.getFullYear(), from.getMonth(), from.getDate());
      while (cur <= to) {
        const k = ymd(cur);
        const list = map.get(k) ?? [];
        list.push(t);
        map.set(k, list);
        cur.setDate(cur.getDate() + 1);
      }
    }
    return map;
  }, [tasks, grid]);

  const todayKey = ymd(today);
  const monthLabel = `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  // Ay değişince açık "tümünü göster" kapanır — aksi halde yeni ayda aynı gün
  // numarası açık kalıyor ve rastgele bir gün genişlemiş görünüyordu.
  const shift = (n: number) => {
    setDayOpen(null);
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + n, 1));
  };

  return (
    <div className="cal">
      <div className="cal-bar">
        <button type="button" className="btn-md ghost" onClick={() => shift(-1)} aria-label="Önceki ay">
          <Icon name="chevLeft" size={14} />
        </button>
        <b className="cal-month">{monthLabel}</b>
        <button type="button" className="btn-md ghost" onClick={() => shift(1)} aria-label="Sonraki ay">
          <Icon name="chevRight" size={14} />
        </button>
        <button type="button" className="btn-md secondary" onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))}>
          Bu ay
        </button>
        <div className="grow" />
        <span className="cal-hint">Görevler planlanan başlangıç–bitiş aralığındaki her güne düşer.</span>
      </div>

      <div className="cal-dow">
        {DOW.map((d) => <div key={d} className="cal-dow-cell">{d}</div>)}
      </div>

      <div className="cal-grid">
        {grid.map((d) => {
          const key = ymd(d);
          const list = byDay.get(key) ?? [];
          const otherMonth = d.getMonth() !== cursor.getMonth();
          const expanded = dayOpen === key;
          const shown = expanded ? list : list.slice(0, MAX_PER_DAY);
          return (
            <div key={key} className={`cal-cell ${otherMonth ? "other" : ""} ${key === todayKey ? "today" : ""}`}>
              <div className="cal-cell-head">
                <span className="cal-daynum mono">{d.getDate()}</span>
                {list.length > 0 ? <span className="cal-count mono">{list.length}</span> : null}
              </div>
              <div className="cal-items">
                {shown.map((t) => {
                  const m = STATUS_META.task[t.status as TaskStatus];
                  return (
                    <button key={`${key}-${t.id}`} type="button" className={`cal-item ${m.cls}`}
                      onClick={() => onOpenTask(t.id)} title={`${t.code} — ${t.title} · ${m.label}`}>
                      {t.isMilestone ? <i className="cal-ms" aria-hidden="true" /> : null}
                      <span className="cal-item-txt">{t.title}</span>
                    </button>
                  );
                })}
                {list.length > MAX_PER_DAY ? (
                  <button type="button" className="cal-more" onClick={() => setDayOpen(expanded ? null : key)}>
                    {expanded ? "daha az" : `+${list.length - MAX_PER_DAY} daha`}
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
